import type { ElementalFxProfile, StageMaterial } from './elyonElementalFx'
export type { StageMaterial } from './elyonElementalFx'

export type StageDamageKind='strike'|'heavy'|'special'|'throw'|'hazard'
export type StageBreakState='intact'|'cracked'|'broken'|'collapsed'
export type StageHazardKind=Exclude<ElementalFxProfile['arena']['hazard'],'none'>

export type StageHazardState={
  elementalFxId:string
  hazardKind:StageHazardKind
  remaining:number
  duration:number
  damagePerSecond:number
  sourceSimulationTick:number
}

export type StageObjectState={
  id:string
  integrity:number
  maxIntegrity:number
  state:StageBreakState
  transitionVersion:number
  material?:StageMaterial
  lastElementalFxId?:string
  hazard?:StageHazardState
}

export type StageDamageEvent={
  objectId:string
  amount:number
  kind:StageDamageKind
  simulationTick:number
  /** Elyon profile id that authored this material response. */
  elementalFxId?:string
  /** Deterministic Fight projection of Elyon's authored arena hazard. */
  hazard?:{
    elementalFxId:string
    hazardKind:StageHazardKind
    duration:number
    damagePerSecond:number
  }
}

export type StageTransition={
  objectId:string
  from:StageBreakState
  to:StageBreakState
  simulationTick:number
  transitionVersion:number
}

function requireFinitePositive(value:number,label:string){
  if(!Number.isFinite(value)||value<=0)throw new Error(`${label} must be finite and positive`)
}
function requireSafeTick(value:number){
  if(!Number.isSafeInteger(value)||value<0)throw new Error('simulationTick must be a non-negative safe integer')
}
function nextTransitionVersion(current:number){
  if(!Number.isSafeInteger(current)||current<0||current>=Number.MAX_SAFE_INTEGER)throw new Error('stage transition version exceeded safe integer range')
  return current+1
}
function validateStageObject(object:StageObjectState){
  if(!object.id.trim())throw new Error('stage object id is required')
  requireFinitePositive(object.maxIntegrity,'maxIntegrity')
  if(!Number.isFinite(object.integrity)||object.integrity<0||object.integrity>object.maxIntegrity)throw new Error('stage object integrity is invalid')
}

export function createStageObject(id:string,maxIntegrity=100,material?:StageMaterial):StageObjectState{
  if(!id.trim())throw new Error('stage object id is required')
  requireFinitePositive(maxIntegrity,'maxIntegrity')
  return{ id,integrity:maxIntegrity,maxIntegrity,state:'intact',transitionVersion:0,...(material?{material}:{}) }
}

export function stageStateFor(integrity:number,maxIntegrity:number):StageBreakState{
  if(!Number.isFinite(integrity)||!Number.isFinite(maxIntegrity)||maxIntegrity<=0)throw new Error('stage integrity must be finite with positive maxIntegrity')
  const ratio=integrity/maxIntegrity
  if(ratio<=0)return'collapsed'
  if(ratio<=.34)return'broken'
  if(ratio<=.7)return'cracked'
  return'intact'
}

export function stageDamageMultiplier(kind:StageDamageKind){
  switch(kind){
    case'special':return 1.5
    case'heavy':return 1.15
    case'throw':return .65
    case'hazard':return 1
    default:return 1
  }
}

function hazardFromEvent(event:StageDamageEvent,nextState:StageBreakState):StageHazardState|undefined{
  const hazard=event.hazard
  if(!hazard||nextState==='collapsed')return undefined
  const duration=Number.isFinite(hazard.duration)?Math.max(0,hazard.duration):0
  const damagePerSecond=Number.isFinite(hazard.damagePerSecond)?Math.max(0,hazard.damagePerSecond):0
  if(duration<=0)return undefined
  return{
    elementalFxId:hazard.elementalFxId,
    hazardKind:hazard.hazardKind,
    remaining:duration,
    duration,
    damagePerSecond,
    sourceSimulationTick:event.simulationTick,
  }
}

export function applyStageDamage(object:StageObjectState,event:StageDamageEvent){
  const safeAmount=Number.isFinite(event.amount)?Math.max(0,event.amount):0
  if(event.objectId!==object.id||object.state==='collapsed'||safeAmount<=0){
    return{object,transition:null as StageTransition|null}
  }
  validateStageObject(object)
  requireSafeTick(event.simulationTick)
  const nextIntegrity=Math.max(0,object.integrity-safeAmount*stageDamageMultiplier(event.kind))
  const nextState=stageStateFor(nextIntegrity,object.maxIntegrity)
  const elementalPatch=event.elementalFxId?{lastElementalFxId:event.elementalFxId}:{}
  const hazard=hazardFromEvent(event,nextState)
  const hazardPatch=hazard?{hazard}:{}
  const {hazard:_existingHazard,...objectWithoutHazard}=object
  const nextBase=nextState==='collapsed'?objectWithoutHazard:object
  if(nextState===object.state){
    return{object:{...nextBase,integrity:nextIntegrity,...elementalPatch,...hazardPatch},transition:null as StageTransition|null}
  }
  const transitionVersion=nextTransitionVersion(object.transitionVersion)
  return{
    object:{...nextBase,integrity:nextIntegrity,state:nextState,transitionVersion,...elementalPatch,...hazardPatch},
    transition:{objectId:object.id,from:object.state,to:nextState,simulationTick:event.simulationTick,transitionVersion} as StageTransition
  }
}

/**
 * Advance one persistent elemental stage reaction using Fight simulation time.
 * Elyon authors lifetime/intensity/profile; Fight remains sovereign over when
 * deterministic simulation time advances (hit-stop, pause and round locks).
 */
export function advanceStageHazard(object:StageObjectState,dt:number,simulationTick:number){
  const hazard=object.hazard
  const safeDt=Number.isFinite(dt)?Math.max(0,dt):0
  if(!hazard||safeDt<=0||object.state==='collapsed')return{object,transition:null as StageTransition|null}
  validateStageObject(object)
  requireSafeTick(simulationTick)
  if(!Number.isFinite(hazard.remaining)||hazard.remaining<0||!Number.isFinite(hazard.duration)||hazard.duration<=0||!Number.isFinite(hazard.damagePerSecond)||hazard.damagePerSecond<0){
    throw new Error('stage hazard state is invalid')
  }

  const elapsed=Math.min(safeDt,Math.max(0,hazard.remaining))
  const nextIntegrity=Math.max(0,object.integrity-hazard.damagePerSecond*elapsed)
  const nextState=stageStateFor(nextIntegrity,object.maxIntegrity)
  const remaining=Math.max(0,hazard.remaining-elapsed)
  const transitionVersion=nextState===object.state?object.transitionVersion:nextTransitionVersion(object.transitionVersion)
  const {hazard:_previousHazard,...base}=object
  const nextHazard=remaining>1e-6&&nextState!=='collapsed'?{...hazard,remaining}:undefined
  const nextObject:StageObjectState={
    ...base,
    integrity:nextIntegrity,
    state:nextState,
    transitionVersion,
    lastElementalFxId:hazard.elementalFxId,
    ...(nextHazard?{hazard:nextHazard}:{}),
  }
  const transition=nextState===object.state?null:{
    objectId:object.id,
    from:object.state,
    to:nextState,
    simulationTick,
    transitionVersion,
  } as StageTransition
  return{object:nextObject,transition}
}

export function advanceStageHazards(objects:StageObjectState[],dt:number,simulationTick:number){
  const transitions:StageTransition[]=[]
  let changed=false
  const nextObjects=objects.map((object)=>{
    const result=advanceStageHazard(object,dt,simulationTick)
    if(result.object!==object)changed=true
    if(result.transition)transitions.push(result.transition)
    return result.object
  })
  return{objects:changed?nextObjects:objects,transitions}
}

export function resetStageObject(object:StageObjectState):StageObjectState{
  return createStageObject(object.id,object.maxIntegrity,object.material)
}

export function serializeStageTransition(transition:StageTransition){
  return`${transition.objectId}:${transition.from}>${transition.to}@${transition.simulationTick}#${transition.transitionVersion}`
}
